/**
 * PolygonPathfinder — shortest-path search inside a `WalkablePolygon`.
 *
 * The painted-scene framework replaces the tile-grid CollisionGrid with an
 * artist-authored irregular polygon. This pathfinder is the polygon-world
 * analogue of `@game/shared`'s `Pathfinder`.
 *
 * Approach: visibility-graph A*.
 *   1. If start→end has line of sight inside the polygon, walk directly.
 *   2. Otherwise build a graph whose nodes are the polygon's reflex vertices
 *      plus the start and end, and connect any two nodes that can "see" each
 *      other through the polygon interior. Run A* on the graph.
 *
 * Reflex (concave) vertices are the only vertices that can lie on a true
 * shortest path — convex vertices on the boundary always have a strictly
 * better detour available — so we restrict the graph to them. For a typical
 * scene polygon (~10–60 vertices) this stays trivially small even with the
 * worst-case O(n²) edge build.
 *
 * Coordinate convention: same as `WalkablePolygon` — all input/output points
 * are normalised to [0, 1]² scene coordinates. Conversion to/from pixels is
 * the caller's job.
 */

import type { Point, WalkablePolygon } from './WalkablePolygon.js';

/** A waypoint along a path in normalised scene coordinates, with optional Z elevation. */
export interface PathPoint extends Point {
  z?: number;
}

interface SearchNode {
  index: number;
  position: PathPoint;
}

/** Lightweight binary heap for the A* open set. */
class MinHeap {
  private data: { key: number; priority: number }[] = [];

  get size(): number {
    return this.data.length;
  }

  push(key: number, priority: number): void {
    this.data.push({ key, priority });
    this.bubbleUp(this.data.length - 1);
  }

  pop(): number {
    const top = this.data[0].key;
    const last = this.data.pop()!;
    if (this.data.length > 0) {
      this.data[0] = last;
      this.sinkDown(0);
    }
    return top;
  }

  private bubbleUp(i: number): void {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.data[i].priority >= this.data[parent].priority) break;
      [this.data[i], this.data[parent]] = [this.data[parent], this.data[i]];
      i = parent;
    }
  }

  private sinkDown(i: number): void {
    const n = this.data.length;
    while (true) {
      let smallest = i;
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      if (l < n && this.data[l].priority < this.data[smallest].priority) smallest = l;
      if (r < n && this.data[r].priority < this.data[smallest].priority) smallest = r;
      if (smallest === i) break;
      [this.data[i], this.data[smallest]] = [this.data[smallest], this.data[i]];
      i = smallest;
    }
  }
}

export class PolygonPathfinder {
  private readonly polygon: WalkablePolygon;

  /** Reflex (concave) vertices, inset slightly toward the centroid. */
  private readonly reflexNodes: ReadonlyArray<PathPoint>;

  constructor(polygon: WalkablePolygon) {
    this.polygon = polygon;
    this.reflexNodes = computeReflexNodes(polygon);
  }

  /** Vertices used as visibility-graph waypoints. Exposed for debugging / tests. */
  get debugReflexNodes(): ReadonlyArray<PathPoint> {
    return this.reflexNodes;
  }

  /**
   * Plan a path from `start` to `end` inside the walkable polygon. Returns the
   * waypoints (start first, end last) in normalised coordinates, or `null` if
   * no path can be found. Endpoints outside the polygon are clamped to the
   * nearest interior point.
   */
  findPath(start: PathPoint, end: PathPoint): PathPoint[] | null {
    const a = this.polygon.closestPointInside(start.x, start.y);
    const b = this.polygon.closestPointInside(end.x, end.y);

    if (sameNormalisedPoint(a, b)) return [this.stampZ(a)];

    if (this.polygon.segmentInside(a.x, a.y, b.x, b.y)) {
      return [this.stampZ(a), this.stampZ(b)];
    }

    const path = this.searchVisibilityGraph(a, b);
    if (path && this.polygon.hasZData) {
      for (let i = 0; i < path.length; i++) {
        path[i] = this.stampZ(path[i]);
      }
    }
    return path;
  }

  private stampZ(p: Point): PathPoint {
    if (!this.polygon.hasZData) return p;
    return { x: p.x, y: p.y, z: this.polygon.interpolateZ(p.x, p.y) };
  }

  /**
   * Convenience overload: same as `findPath` but returns `[a, b]` directly
   * when the line of sight is clear, skipping the visibility-graph build.
   * Useful when callers only want to know "can the walker walk straight?"
   */
  hasLineOfSight(a: PathPoint, b: PathPoint): boolean {
    return this.polygon.segmentInside(a.x, a.y, b.x, b.y);
  }

  /**
   * Drop waypoints that are collinear with their neighbours. Keeps direction
   * changes only — mirrors `Pathfinder.simplifyPath` behaviour.
   */
  static simplifyPath(path: PathPoint[]): PathPoint[] {
    if (path.length <= 2) return path.slice();
    const result: PathPoint[] = [path[0]];
    for (let i = 1; i < path.length - 1; i++) {
      const prev = path[i - 1];
      const curr = path[i];
      const next = path[i + 1];
      // Use sign-based direction comparison so float jitter does not register
      // as a turn. For diagonal motion this is a coarse-but-faithful match
      // to the grid pathfinder's behaviour.
      const dx1 = Math.sign(curr.x - prev.x);
      const dy1 = Math.sign(curr.y - prev.y);
      const dx2 = Math.sign(next.x - curr.x);
      const dy2 = Math.sign(next.y - curr.y);
      if (dx1 !== dx2 || dy1 !== dy2) {
        result.push(curr);
      }
    }
    result.push(path[path.length - 1]);
    return result;
  }

  // ── internals ────────────────────────────────────────────────────────────

  private searchVisibilityGraph(start: PathPoint, end: PathPoint): PathPoint[] | null {
    const nodes: SearchNode[] = [
      { index: 0, position: start },
      { index: 1, position: end },
      ...this.reflexNodes.map((p, i) => ({ index: i + 2, position: p })),
    ];
    const startIndex = 0;
    const endIndex = 1;

    // Adjacency built lazily — we visit at most O(n²) edges and most A* runs
    // expand far fewer.
    const visible = (a: SearchNode, b: SearchNode): boolean => {
      if (a.index === b.index) return false;
      return this.polygon.segmentInside(a.position.x, a.position.y, b.position.x, b.position.y);
    };

    const distance = (a: SearchNode, b: SearchNode): number =>
      Math.hypot(a.position.x - b.position.x, a.position.y - b.position.y);

    const gScore = new Map<number, number>();
    const cameFrom = new Map<number, number>();
    const closed = new Set<number>();

    gScore.set(startIndex, 0);

    const heuristic = (n: SearchNode): number => distance(n, nodes[endIndex]);

    const open = new MinHeap();
    open.push(startIndex, heuristic(nodes[startIndex]));

    while (open.size > 0) {
      const currentKey = open.pop();
      if (closed.has(currentKey)) continue;
      if (currentKey === endIndex) {
        return this.reconstruct(cameFrom, nodes, endIndex);
      }
      closed.add(currentKey);

      const current = nodes[currentKey];
      const currentG = gScore.get(currentKey)!;

      for (const neighbour of nodes) {
        if (closed.has(neighbour.index)) continue;
        if (!visible(current, neighbour)) continue;

        const tentativeG = currentG + distance(current, neighbour);
        const prev = gScore.get(neighbour.index);
        if (prev === undefined || tentativeG < prev) {
          gScore.set(neighbour.index, tentativeG);
          cameFrom.set(neighbour.index, currentKey);
          open.push(neighbour.index, tentativeG + heuristic(neighbour));
        }
      }
    }

    return null;
  }

  private reconstruct(
    cameFrom: Map<number, number>,
    nodes: ReadonlyArray<SearchNode>,
    endIndex: number,
  ): PathPoint[] {
    const path: PathPoint[] = [];
    let cursor: number | undefined = endIndex;
    while (cursor !== undefined) {
      path.push(nodes[cursor].position);
      cursor = cameFrom.get(cursor);
    }
    path.reverse();
    return path;
  }
}

// ── helpers ────────────────────────────────────────────────────────────────

function sameNormalisedPoint(a: PathPoint, b: PathPoint): boolean {
  return Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9;
}

/**
 * Identify reflex (concave) vertices — the only vertices that can lie on a
 * shortest path. Convex vertices are skipped because the polygon interior
 * always offers a strictly shorter detour around them.
 *
 * Reflex vertices are returned at their raw boundary positions. `WalkablePolygon`'s
 * `segmentInside` already handles shared-endpoint cases via its proper-cross
 * test, so a path edge ending exactly at a reflex vertex is allowed even
 * though the vertex sits on the boundary.
 */
function computeReflexNodes(polygon: WalkablePolygon): PathPoint[] {
  const pts = polygon.points;
  const n = pts.length;
  if (n < 3) return [];

  // Polygon winding determined by summing cross products at every vertex —
  // identical in sign to the standard shoelace formula. Convex vertices share
  // the polygon's orientation sign; reflex vertices oppose it.
  let crossSum = 0;
  const crosses: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n];
    const curr = pts[i];
    const next = pts[(i + 1) % n];
    const cross =
      (curr.x - prev.x) * (next.y - curr.y) - (curr.y - prev.y) * (next.x - curr.x);
    crosses[i] = cross;
    crossSum += cross;
  }
  const orientation = Math.sign(crossSum) || 1;

  const reflex: PathPoint[] = [];
  for (let i = 0; i < n; i++) {
    if (Math.sign(crosses[i]) === orientation) continue;
    reflex.push({ x: pts[i].x, y: pts[i].y });
  }
  return reflex;
}
